import { Box, IconButton } from "@mui/material";
import { Link } from "react-router";
import React from "react";
import { getTaskDeadLine, getTaskResponsible, getTaskSubTasks } from "./Task.tsx";
import { TaskResponsibleViewModel } from "../TaskManagement.types.ts";
import { TaskLaneStateEnum } from "../../annualWheel/AnnualWheel.types.ts";
import { useTranslation } from "@localization/useTranslation.ts";
import { useTaskStyles } from "./Task.styles.ts";
import { primaryArrowRight } from "@common/icons";
import { useContentSize } from "../../main/content/ContentSizeContext.tsx";

export interface TaskFooterProps {
    responsibles: Array<TaskResponsibleViewModel>;
    deadline: Date;
    subTaskCompleted: number;
    totalSubTasks: number;
    taskStatus: TaskLaneStateEnum;
    isOverdue: boolean;
    taskId: string;
}

function TaskFooter(props: TaskFooterProps) {
    const { translateString, translateDate } = useTranslation();
    const { isOnMediumScreen } = useContentSize();

    const styles = useTaskStyles(props.taskStatus, props.isOverdue);

    const getTaskLinkUrl = () => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("taskId", props.taskId);
        return `?${searchParams.toString()}`;
    };

    const arrowBox = (
        <Box sx={styles.arrowContainer}>
            <IconButton component={Link} to={getTaskLinkUrl()} sx={[styles.arrow, isOnMediumScreen && { padding: 0, width: 24, height: 24 }]}>
                {primaryArrowRight}
            </IconButton>
        </Box>
    );

    const showSubTasks = props.totalSubTasks > 0;

    // In column mode, force each helper's row into a 24px-icon + 1fr-text grid so the icons
    // (Calendar SVG, Avatar circle, Checkbox SVG — all with different intrinsic widths) sit in a
    // uniform column and the text after them starts at the same X on every row.
    // !important is required because the helper's own sx and our sx generate same-specificity classes.
    const normalizedRowSx = {
        "& > div": {
            display: "grid !important",
            gridTemplateColumns: "24px 1fr",
            alignItems: "center",
            columnGap: 1,
        },
        "& > div > :first-child": {
            justifySelf: "center",
        },
        "& > div > :nth-child(2)": {
            marginLeft: "0 !important",
        },
    };

    if (isOnMediumScreen) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={normalizedRowSx}>{getTaskDeadLine(styles, translateDate(props.deadline))}</Box>

                {showSubTasks ? (
                    <>
                        <Box sx={normalizedRowSx}>{getTaskResponsible(props.responsibles, styles, translateString)}</Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box sx={normalizedRowSx}>{getTaskSubTasks(styles, props.subTaskCompleted, props.totalSubTasks)}</Box>
                            {arrowBox}
                        </Box>
                    </>
                ) : (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box sx={normalizedRowSx}>{getTaskResponsible(props.responsibles, styles, translateString)}</Box>
                        {arrowBox}
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>{getTaskDeadLine(styles, translateDate(props.deadline))}</Box>
            <Box>{getTaskResponsible(props.responsibles, styles, translateString)}</Box>
            {showSubTasks && <Box>{getTaskSubTasks(styles, props.subTaskCompleted, props.totalSubTasks)}</Box>}
            {arrowBox}
        </Box>
    );
}

export default TaskFooter;
